/**
 * A proposal's cover, as the web draws it: the page the PDF prints first.
 *
 * Logo large at the top with the quotation number and date opposite, the
 * cover photo edge to edge, the title, a line of introduction, who and where
 * it is for, and the business's contact details on a band at the foot. It is
 * the page, not a card on the page: the caller supplies the sheet's edge
 * (rounded on the customer's screen, flush with the canvas paper). The
 * customer's page and the builder's canvas both draw this, with the canvas
 * passing inputs where the page passes text, so the cover the office edits is
 * the one that is sent. Decision 83.
 */
import { letterheadLines, proposalHeading } from '@fieldops/shared'
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { logoSrc } from '../../lib/files.js'
import type { LetterheadBusiness } from './Letterhead.js'

export interface CoverDetail {
  label: string
  value: string | null | undefined
}

export function CoverSheet({
  business,
  number,
  date,
  photo,
  title,
  intro,
  details,
  className,
}: {
  business: LetterheadBusiness
  number: string
  date: string | null
  /** Absent when there is no cover photo; the title then has the room. */
  photo: ReactNode
  title: ReactNode
  intro: ReactNode
  details: CoverDetail[]
  className?: string
}) {
  const logo = logoSrc(business.logoUrl)
  const contact = letterheadLines(business)
  const shown = details.filter((detail) => detail.value)

  return (
    <section
      aria-label="Cover"
      className={cn('overflow-hidden bg-white font-proposal', className)}
    >
      <div className="h-2 bg-[var(--p-brand)]" />

      <header className="flex items-start justify-between gap-6 px-6 pt-7 sm:px-10 sm:pt-9">
        <img src={logo} alt={business.name} className="h-16 w-auto max-w-[60%] object-contain object-left sm:h-20" />
        <div className="pt-1 text-right text-xs sm:text-[13px]">
          <p className="font-bold text-ink-900">{proposalHeading(number)}</p>
          {date ? <p className="text-ink-700">{date}</p> : null}
        </div>
      </header>

      {photo ? <div className="mt-7 sm:mt-9">{photo}</div> : null}

      <div className={cn('px-6 pb-8 sm:px-10 sm:pb-10', photo ? 'pt-7 sm:pt-9' : 'pt-14 sm:pt-20')}>
        <p className="text-[11px] font-bold tracking-[0.2em] text-[var(--p-accent-text)] uppercase">Proposal</p>
        <div className="mt-2">{title}</div>
        <div aria-hidden className="mt-5 h-1 w-16 rounded-full bg-[var(--p-brand)]" />
        {intro ? <div className="mt-5">{intro}</div> : null}

        {shown.length > 0 ? (
          <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-ink-200 pt-6 text-sm sm:grid-cols-2">
            {shown.map((detail) => (
              <div key={detail.label} className="min-w-0">
                <dt className="text-[10px] font-bold tracking-[0.14em] text-[var(--p-accent-text)] uppercase">
                  {detail.label}
                </dt>
                <dd className="mt-1 font-semibold break-words whitespace-pre-line text-ink-900">{detail.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      {contact.length > 0 ? (
        <footer className="flex flex-wrap gap-x-5 gap-y-1 bg-[var(--p-accent)] px-6 py-4 text-xs text-[var(--p-on-accent)] sm:px-10">
          {contact.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </footer>
      ) : null}
    </section>
  )
}
